import { useState, useEffect } from 'react';
import { MapPin, ArrowRight, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { Opportunity } from '@/types';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const typeLabels: Record<string, string> = {
  emploi: 'Emploi',
  stage: 'Stage',
  bourse: 'Bourse',
  formation: 'Formation',
  volontariat: 'Volontariat'
};

const typeColors: Record<string, string> = {
  emploi: 'bg-blue-100 text-blue-700',
  stage: 'bg-amber-100 text-amber-700',
  bourse: 'bg-emerald-100 text-emerald-700',
  formation: 'bg-orange-100 text-orange-700',
  volontariat: 'bg-rose-100 text-rose-700'
};

export function FeaturedOpportunities() {
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);

  useEffect(() => {
    const fetchOpportunities = async () => {
      try {
        const { data, error } = await supabase
          .from('opportunities')
          .select('*')
          .eq('status', 'approved')
          .order('created_at', { ascending: false })
          .limit(6);

        if (error) throw error;

        const transformed: Opportunity[] = (data || []).map((opp: any) => ({
          id: opp.id,
          title: opp.title,
          company: opp.company,
          type: opp.type,
          location: opp.location,
          arrondissement: opp.arrondissement,
          description: opp.description,
          responsibilities: opp.responsibilities,
          requirements: opp.requirements,
          salary: opp.salary || opp.scholarship_amount,
          deadline: opp.deadline ? new Date(opp.deadline) : new Date(),
          publishedAt: new Date(opp.created_at),
          applicationUrl: opp.application_url,
          phoneNumber: opp.contact_phone,
          featured: true,
        }));

        setOpportunities(transformed);
      } catch (error) {
        console.error('Error loading opportunities:', error);
      }
    };

    fetchOpportunities();
  }, []);

  if (opportunities.length === 0) {
    return (
      <section className="py-20 lg:py-28 bg-white">
        <div className="container mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
            Opportunités à la une
          </h2>
          <p className="text-lg text-slate-500 mb-8">
            Aucune opportunité disponible pour le moment.
          </p>
          <Link to="/publish-opportunity">
            <Button className="bg-honor-orange hover:bg-honor-orange/90">
              Publiez la première offre
            </Button>
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 lg:py-28 bg-white">
      <div className="container mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3 tracking-tight">
              Opportunités à la une
            </h2>
            <p className="text-lg text-slate-600">
              Les dernières offres ajoutées sur la plateforme
            </p>
          </div>
          <Link to="/opportunities">
            <Button variant="outline" className="border-slate-300 text-slate-700 hover:bg-slate-100">
              Voir tout
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {opportunities.map((opp) => (
            <Link
              key={opp.id}
              to={`/opportunity/${opp.id}`}
              className="group block bg-slate-50 hover:bg-white border border-slate-200 hover:border-slate-300 rounded-2xl p-6 transition-all hover:shadow-lg hover:-translate-y-1"
            >
              {/* Header */}
              <div className="flex items-start justify-between gap-4 mb-4">
                <div className="w-12 h-12 rounded-xl bg-slate-200 flex items-center justify-center text-slate-600 font-bold text-sm">
                  {opp.company.substring(0, 2).toUpperCase()}
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${typeColors[opp.type]}`}>
                  {typeLabels[opp.type]}
                </span>
              </div>

              {/* Content */}
              <h3 className="text-lg font-semibold text-slate-900 mb-1 group-hover:text-honor-orange transition-colors line-clamp-1">
                {opp.title}
              </h3>
              <p className="text-sm text-slate-500 mb-3">{opp.company}</p>
              
              <p className="text-sm text-slate-600 line-clamp-2 mb-4">
                {opp.description}
              </p>

              {/* Meta */}
              <div className="flex items-center gap-4 text-sm text-slate-500">
                <div className="flex items-center gap-1">
                  <MapPin className="h-4 w-4" />
                  <span>{opp.arrondissement || opp.location}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  <span>{format(opp.deadline, 'd MMM', { locale: fr })}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
